import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { useProducts } from "../../../context/ProductsContext";
import { useClientGroups } from "../../../hooks/useClientGroups";
import {
  RuleCondition,
  RuleConnector,
  RuleGroup,
  RuleTree,
} from "../../../types/clientGroups";
import {
  MAX_RULE_DEPTH,
  RULE_KIND_OPTIONS,
  RuleKind,
  addChildAt,
  applyCountSubject,
  applyGotTarget,
  applyInLast,
  conditionKind,
  countSubject,
  emptyCondition,
  emptySubgroup,
  gotTargetValue,
  groupDepth,
  inLast,
  isGroup,
  kindToCondition,
  normalizeTree,
  readsAs,
  removeNodeAt,
  setConnectorAt,
  updateNodeAt,
} from "./ruleModel";
import { fieldStyles, dashedPill } from "./fieldStyles";

interface Props {
  value: RuleTree;
  onChange: (rules: RuleTree) => void;
  /** Count strip position — editor renders it on top, the modal below. */
  countPlacement?: "top" | "bottom";
}

interface Opt {
  value: string;
  label: string;
}

const lead = (t: string) => (
  <Text size="13px" c="dimmed">
    {t}
  </Text>
);

const dateStr = (d: unknown): string | undefined => {
  if (!d) return undefined;
  if (typeof d === "string") return d;
  const dt = new Date(d as any);
  return isNaN(dt.getTime()) ? undefined : dt.toISOString().slice(0, 10);
};

const groupCountLabel = (n: number | null): string => {
  if (n == null) return "…";
  if (n < 10) return "under 10 clients";
  return `${n.toLocaleString()} clients`;
};

// Field option lists + the tree operations, threaded to every group/condition.
interface Ctx {
  serviceOpts: Opt[];
  staffOpts: Opt[];
  gotTargetData: { group: string; items: Opt[] }[];
  productIds: Set<string>;
  previewRules: ReturnType<typeof useClientGroups>["previewRules"];
  setKind: (path: number[], kind: RuleKind) => void;
  setParam: (path: number[], key: string, v: unknown) => void;
  updateCond: (path: number[], fn: (c: RuleCondition) => RuleCondition) => void;
  addCondition: (groupPath: number[]) => void;
  addSubgroup: (groupPath: number[], parent: RuleConnector) => void;
  remove: (path: number[]) => void;
  setConn: (groupPath: number[], conn: RuleConnector) => void;
}

// --- per-group live match count (debounced) --------------------------------
// `enabled` is false for the root group — its count is already computed by the
// builder's root count strip, so fetching it here again would double every
// root-level preview request.
function useSubtreeCount(
  subtree: RuleGroup,
  previewRules: Ctx["previewRules"],
  enabled: boolean = true,
): number | null {
  const [n, setN] = useState<number | null>(null);
  const key = JSON.stringify(subtree);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await previewRules(JSON.parse(key) as RuleTree, 0);
        if (!cancelled) setN(r.total);
      } catch {
        if (!cancelled) setN(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [key, previewRules, enabled]);
  return n;
}

// --- one condition row ------------------------------------------------------
function ConditionRow({
  path,
  cond,
  ctx,
}: {
  path: number[];
  cond: RuleCondition;
  ctx: Ctx;
}) {
  const p = cond.params;
  const kind = conditionKind(cond);

  const numInput = (key: string, val: number | undefined, w = 66) => (
    <NumberInput
      styles={fieldStyles}
      size="xs"
      w={w}
      min={0}
      clampBehavior="none"
      hideControls
      value={val ?? ""}
      onChange={(v) =>
        ctx.setParam(path, key, v === "" ? undefined : Number(v))
      }
    />
  );

  const countRange = (
    <>
      {lead("at least")}
      {numInput("min_count", p.min_count)}
      {lead("and under")}
      {numInput("max_count", p.max_count)}
      {lead("times")}
    </>
  );

  const inLastFragment = (() => {
    const { value: v, unit } = inLast(cond);
    return (
      <>
        {lead("in the last")}
        <NumberInput
          styles={fieldStyles}
          size="xs"
          w={66}
          min={0}
          clampBehavior="none"
          hideControls
          value={v ?? ""}
          onChange={(nv) =>
            ctx.updateCond(path, (c) =>
              applyInLast(c, nv === "" ? undefined : Number(nv), unit),
            )
          }
        />
        <SegmentedControl
          size="xs"
          data={[
            { value: "days", label: "days" },
            { value: "weeks", label: "weeks" },
          ]}
          value={unit}
          onChange={(u) =>
            ctx.updateCond(path, (c) =>
              applyInLast(c, v, u as "days" | "weeks"),
            )
          }
        />
      </>
    );
  })();

  const eventDates = (
    <>
      {lead("after")}
      <DateInput
        styles={fieldStyles}
        size="xs"
        w={140}
        clearable
        placeholder="any date"
        value={(p.after_date as any) ?? null}
        onChange={(d) => ctx.setParam(path, "after_date", dateStr(d))}
      />
      {lead("before")}
      <DateInput
        styles={fieldStyles}
        size="xs"
        w={140}
        clearable
        placeholder="any date"
        value={(p.before_date as any) ?? null}
        onChange={(d) => ctx.setParam(path, "before_date", dateStr(d))}
      />
    </>
  );

  const fragments = () => {
    if (kind === "got") {
      const target = gotTargetValue(cond);
      const isProduct = cond.type === "product_purchased";
      return (
        <>
          <Select
            styles={fieldStyles}
            size="xs"
            w={190}
            data={ctx.gotTargetData}
            searchable
            value={target}
            onChange={(v) =>
              ctx.updateCond(path, (c) =>
                applyGotTarget(
                  c,
                  v ?? "any_service",
                  v === "any_product" || ctx.productIds.has(v ?? ""),
                ),
              )
            }
          />
          {!isProduct && (
            <>
              {lead("from")}
              <Select
                styles={fieldStyles}
                size="xs"
                w={140}
                placeholder="any staff"
                data={ctx.staffOpts}
                searchable
                clearable
                value={p.staff_id ?? null}
                onChange={(v) => ctx.setParam(path, "staff_id", v ?? undefined)}
              />
            </>
          )}
          {countRange}
          {inLastFragment}
          {eventDates}
        </>
      );
    }
    if (kind === "saw") {
      return (
        <>
          <Select
            styles={fieldStyles}
            size="xs"
            w={160}
            placeholder="any staff"
            data={ctx.staffOpts}
            searchable
            clearable
            value={p.staff_id ?? null}
            onChange={(v) => ctx.setParam(path, "staff_id", v ?? undefined)}
          />
          {countRange}
          {inLastFragment}
          {eventDates}
        </>
      );
    }
    if (kind === "count") {
      return (
        <>
          {lead("at least")}
          {numInput("min_count", p.min_count)}
          {lead("and under")}
          {numInput("max_count", p.max_count)}
          <SegmentedControl
            size="xs"
            data={[
              { value: "appointments", label: "appointments" },
              { value: "purchases", label: "product purchases" },
            ]}
            value={countSubject(cond)}
            onChange={(s) =>
              ctx.updateCond(path, (c) =>
                applyCountSubject(c, s as "appointments" | "purchases"),
              )
            }
          />
        </>
      );
    }
    if (kind === "spend") {
      return (
        <>
          {lead("over $")}
          {numInput("min", p.min, 90)}
          {lead("and under $")}
          {numInput("max", p.max, 90)}
        </>
      );
    }
    if (kind === "all") {
      return lead("— everyone at this location —");
    }
    // client_since
    return (
      <>
        {lead("after")}
        <DateInput
          styles={fieldStyles}
          size="xs"
          w={150}
          clearable
          placeholder="any date"
          value={(p.after as any) ?? null}
          onChange={(d) => ctx.setParam(path, "after", dateStr(d))}
        />
        {lead("before")}
        <DateInput
          styles={fieldStyles}
          size="xs"
          w={150}
          clearable
          placeholder="any date"
          value={(p.before as any) ?? null}
          onChange={(d) => ctx.setParam(path, "before", dateStr(d))}
        />
      </>
    );
  };

  return (
    <Paper withBorder radius="md" p="12px 14px" mb={6}>
      <Group gap="8px 10px" align="center" wrap="wrap">
        {lead("Clients who")}
        <Select
          styles={fieldStyles}
          size="xs"
          w={210}
          data={RULE_KIND_OPTIONS}
          value={kind}
          onChange={(v) => ctx.setKind(path, (v as RuleKind) ?? "got")}
          allowDeselect={false}
        />
        {fragments()}
        <ActionIcon
          variant="subtle"
          color="gray"
          ml="auto"
          aria-label="Remove condition"
          onClick={() => ctx.remove(path)}
        >
          <IconTrash size={15} stroke={2} />
        </ActionIcon>
      </Group>
    </Paper>
  );
}

// --- one group (recursively renders its children) --------------------------
function GroupNode({
  group,
  path,
  ctx,
  rootCount,
}: {
  group: RuleGroup;
  path: number[];
  ctx: Ctx;
  /** For the root group only: the count already computed by the count strip. */
  rootCount?: number | null;
}) {
  const isRoot = path.length === 0;
  const depth = groupDepth(path);
  const subtreeCount = useSubtreeCount(group, ctx.previewRules, !isRoot);
  const count = isRoot ? (rootCount ?? null) : subtreeCount;

  const body = (
    <Stack gap={0}>
      <Group gap={10} align="center" mb={6} wrap="nowrap">
        <SegmentedControl
          size="xs"
          w={116}
          data={[
            { value: "AND", label: "All" },
            { value: "OR", label: "Any" },
          ]}
          value={group.connector}
          onChange={(v) => ctx.setConn(path, v as RuleConnector)}
        />
        <Text size="13px" c="dimmed" style={{ whiteSpace: "nowrap" }}>
          {isRoot ? "of the following must be true" : "of the following"}
        </Text>
        <Divider style={{ flex: 1 }} />
        <Text
          size="12.5px"
          c="dimmed"
          style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          {groupCountLabel(count)}
        </Text>
        {!isRoot && (
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Remove group"
            onClick={() => ctx.remove(path)}
          >
            <IconTrash size={15} stroke={2} />
          </ActionIcon>
        )}
      </Group>

      {group.children.map((child, i) =>
        isGroup(child) ? (
          <GroupNode key={i} group={child} path={[...path, i]} ctx={ctx} />
        ) : (
          <ConditionRow key={i} path={[...path, i]} cond={child} ctx={ctx} />
        ),
      )}

      <Group gap={8} mt={2} mb={4}>
        <Button
          variant="default"
          size="xs"
          leftSection={<IconPlus size={13} stroke={2.4} />}
          onClick={() => ctx.addCondition(path)}
          styles={dashedPill}
        >
          Condition
        </Button>
        {depth < MAX_RULE_DEPTH ? (
          <Button
            variant="default"
            size="xs"
            leftSection={<IconPlus size={13} stroke={2.4} />}
            onClick={() => ctx.addSubgroup(path, group.connector)}
            styles={dashedPill}
          >
            Subgroup
          </Button>
        ) : (
          <Text size="12.5px" c="dimmed">
            Deepest nesting level — add conditions here.
          </Text>
        )}
      </Group>
    </Stack>
  );

  if (isRoot) return body;
  // Nested groups draw a spine down their left gutter.
  return (
    <Box
      style={{
        borderLeft: "1px solid var(--mantine-color-gray-3)",
        paddingLeft: 14,
        marginLeft: 2,
        marginBottom: 6,
      }}
    >
      {body}
    </Box>
  );
}

export default function SentenceRuleBuilder({
  value,
  onChange,
  countPlacement = "top",
}: Props) {
  const { services, employees } = useSalon() as any;
  const { products } = useProducts() as any;
  const { previewRules } = useClientGroups();

  // Normalize legacy shapes to the recursive tree the builder edits.
  const tree = useMemo(() => normalizeTree(value), [value]);

  const serviceOpts: Opt[] = useMemo(
    () =>
      (services || []).map((s: any) => ({
        value: String(s.uuid ?? s.id ?? s.salon_service_id),
        label: s.name ?? "Service",
      })),
    [services],
  );
  const staffOpts: Opt[] = useMemo(
    () =>
      (employees || []).map((e: any) => ({
        value: String(e.id ?? e.staff_user_id),
        label:
          e.name ||
          `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() ||
          "Staff",
      })),
    [employees],
  );
  const productOpts: Opt[] = useMemo(
    () =>
      (products || []).map((p: any) => ({
        value: String(p.id),
        label: p.name ?? "Product",
      })),
    [products],
  );
  const gotTargetData = useMemo(
    () => [
      {
        group: "Any",
        items: [
          { value: "any_service", label: "any service" },
          { value: "any_product", label: "any product" },
        ],
      },
      ...(serviceOpts.length
        ? [{ group: "Services", items: serviceOpts }]
        : []),
      ...(productOpts.length
        ? [{ group: "Products", items: productOpts }]
        : []),
    ],
    [serviceOpts, productOpts],
  );
  const productIds = useMemo(
    () => new Set(productOpts.map((o) => o.value)),
    [productOpts],
  );

  // --- root count strip (MATCHES / OPTED IN), debounced --------------------
  const [counts, setCounts] = useState<{ total: number; opted: number } | null>(
    null,
  );
  const [counting, setCounting] = useState(false);
  const rulesKey = JSON.stringify(tree);
  useEffect(() => {
    let cancelled = false;
    setCounting(true);
    const t = setTimeout(async () => {
      try {
        const res = await previewRules(JSON.parse(rulesKey) as RuleTree, 0);
        if (!cancelled)
          setCounts({ total: res.total, opted: res.opted_in_total ?? 0 });
      } catch {
        if (!cancelled) setCounts(null);
      } finally {
        if (!cancelled) setCounting(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [rulesKey, previewRules]);

  // --- tree operations (single emit each) ----------------------------------
  const ctx: Ctx = useMemo(
    () => ({
      serviceOpts,
      staffOpts,
      gotTargetData,
      productIds,
      previewRules,
      setKind: (path, kind) =>
        onChange(updateNodeAt(tree, path, () => kindToCondition(kind, {}))),
      setParam: (path, key, v) =>
        onChange(
          updateNodeAt(tree, path, (c) => {
            const params: any = { ...(c as RuleCondition).params };
            if (v === undefined || v === "" || v === null) delete params[key];
            else params[key] = v;
            return { ...(c as RuleCondition), params };
          }),
        ),
      updateCond: (path, fn) =>
        onChange(updateNodeAt(tree, path, (c) => fn(c as RuleCondition))),
      addCondition: (groupPath) =>
        onChange(addChildAt(tree, groupPath, emptyCondition("got"))),
      addSubgroup: (groupPath, parent) =>
        onChange(addChildAt(tree, groupPath, emptySubgroup(parent))),
      remove: (path) => onChange(removeNodeAt(tree, path)),
      setConn: (groupPath, conn) =>
        onChange(setConnectorAt(tree, groupPath, conn)),
    }),
    [
      tree,
      onChange,
      serviceOpts,
      staffOpts,
      gotTargetData,
      productIds,
      previewRules,
    ],
  );

  const readsAsLine = (
    <Box>
      <Text
        size="11px"
        fw={600}
        c="dimmed"
        tt="uppercase"
        style={{ letterSpacing: ".06em" }}
        mb={4}
      >
        Reads as
      </Text>
      <Text size="12.5px" style={{ lineHeight: 1.6 }}>
        {readsAs(tree)}
      </Text>
    </Box>
  );

  const countStrip = (
    <Paper withBorder radius="md" p="14px 18px">
      <Group gap={26} align="flex-start" wrap="wrap">
        <Box>
          <Text
            size="11px"
            fw={600}
            c="dimmed"
            tt="uppercase"
            style={{ letterSpacing: ".06em" }}
          >
            Matches
          </Text>
          <Text
            size="24px"
            fw={600}
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {counting && !counts ? "…" : (counts?.total ?? 0).toLocaleString()}
          </Text>
        </Box>
        <Divider orientation="vertical" />
        <Box>
          <Text
            size="11px"
            fw={600}
            c="dimmed"
            tt="uppercase"
            style={{ letterSpacing: ".06em" }}
          >
            Opted in to marketing
          </Text>
          <Text
            size="24px"
            fw={600}
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {counting && !counts ? "…" : (counts?.opted ?? 0).toLocaleString()}
          </Text>
        </Box>
        <Text size="12.5px" c="dimmed" maw={200} ml="auto">
          An estimate, recalculated as you edit. Membership is calculated live
          when the group is used.
        </Text>
      </Group>
    </Paper>
  );

  return (
    <Stack gap={12}>
      {countPlacement === "top" && countStrip}
      {readsAsLine}
      <GroupNode
        group={tree}
        path={[]}
        ctx={ctx}
        rootCount={counts?.total ?? null}
      />
      {countPlacement === "bottom" && (
        <>
          <Divider mt={8} />
          {countStrip}
        </>
      )}
    </Stack>
  );
}
